import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Label,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import BreadCrumb from "../../../../../Components/Common/BreadCrumb";
import HelpArticleLink from "../../../../../Components/Common/HelpArticleLink";
import { HELP_ARTICLE_DRE } from "../../../../../helpers/helpCenter";
import { formatMoneyFromCents } from "../../../../../helpers/money";
import {
  EXPENSE_CATEGORIES,
  EXPENSE_COST_TYPES,
  ExpenseCategory,
  ExpenseCostType,
} from "../../../../../services/expenseService";
import { getUnits, Unit } from "../../../../../services/unitService";
import {
  getUnitPlReport,
  UnitPlReport,
} from "../../../../../services/unitPlReportService";

const UNITS_PAGE_SIZE = 100;

const firstDayOfCurrentMonth = (): string => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");

  return `${now.getFullYear()}-${month}-01`;
};

const todayIsoDate = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const formatPeriodLabel = (from: string, to: string, locale: string): string => {
  const formatter = new Intl.DateTimeFormat(locale, { dateStyle: "medium" });
  const fromDate = new Date(`${from}T00:00:00`);
  const toDate = new Date(`${to}T00:00:00`);

  return `${formatter.format(fromDate)} — ${formatter.format(toDate)}`;
};

const DreReport = () => {
  const { t, i18n } = useTranslation();

  document.title = t("studio.financial.dre.pageTitle");

  const formatMoney = useCallback(
    (cents: number) => formatMoneyFromCents(cents, i18n.language),
    [i18n.language]
  );

  const [units, setUnits] = useState<Unit[]>([]);
  const [unitId, setUnitId] = useState("");
  const [fromDate, setFromDate] = useState(firstDayOfCurrentMonth);
  const [toDate, setToDate] = useState(todayIsoDate);
  const [report, setReport] = useState<UnitPlReport | null>(null);
  const [loadingUnits, setLoadingUnits] = useState(true);
  const [loadingReport, setLoadingReport] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const resolveUnitName = useCallback(
    (id: number): string => units.find((unit) => unit.id === id)?.name ?? String(id),
    [units]
  );

  const resolveCategoryLabel = (category: ExpenseCategory): string =>
    t(`studio.financial.expenses.category.${category}`);

  const resolveCostTypeLabel = (costType: ExpenseCostType): string =>
    t(`studio.financial.expenses.costType.${costType}`);

  const loadUnits = useCallback(async () => {
    setLoadingUnits(true);
    try {
      const response = await getUnits({ per_page: UNITS_PAGE_SIZE });
      setUnits(response.data);
      if (response.data.length === 1) {
        setUnitId(String(response.data[0].id));
      }
    } catch (error: any) {
      toast.error(error?.data || t("studio.financial.dre.toast.loadUnitsError"));
    } finally {
      setLoadingUnits(false);
    }
  }, [t]);

  const fetchReport = useCallback(async () => {
    if (!unitId || !fromDate || !toDate) {
      toast.error(t("studio.financial.dre.validation.filtersRequired"));
      return;
    }

    if (fromDate > toDate) {
      toast.error(t("studio.financial.dre.validation.periodInvalid"));
      return;
    }

    setLoadingReport(true);
    setHasSearched(true);

    try {
      const response = await getUnitPlReport({
        unit_id: Number(unitId),
        from: fromDate,
        to: toDate,
      });
      setReport(response.data);
    } catch (error: any) {
      setReport(null);
      toast.error(error?.data || t("studio.financial.dre.toast.loadError"));
    } finally {
      setLoadingReport(false);
    }
  }, [unitId, fromDate, toDate, t]);

  useEffect(() => {
    loadUnits();
  }, [loadUnits]);

  const summaryCards = useMemo(() => {
    if (!report) {
      return [];
    }

    const netColor =
      report.net_result_cents > 0
        ? "success"
        : report.net_result_cents < 0
          ? "danger"
          : "secondary";

    return [
      {
        id: "revenue",
        label: t("studio.financial.dre.cards.revenue"),
        value: report.revenue_cents,
        icon: "ri-arrow-up-circle-line",
        color: "success",
      },
      {
        id: "expenses",
        label: t("studio.financial.dre.cards.expenses"),
        value: report.expenses_cents,
        icon: "ri-arrow-down-circle-line",
        color: "warning",
      },
      {
        id: "net",
        label: t("studio.financial.dre.cards.netResult"),
        value: report.net_result_cents,
        icon: "ri-funds-line",
        color: netColor,
      },
    ];
  }, [report, t]);

  const categoryRows = useMemo(() => {
    if (!report) {
      return [];
    }

    const totals = new Map<ExpenseCategory, number>();
    EXPENSE_CATEGORIES.forEach((category) => totals.set(category, 0));

    report.expenses_by_category.forEach((row) => {
      totals.set(row.category, row.total_cents);
    });

    return EXPENSE_CATEGORIES.map((category) => ({
      category,
      total_cents: totals.get(category) ?? 0,
    })).filter((row) => row.total_cents > 0);
  }, [report]);

  const costTypeRows = useMemo(() => {
    if (!report) {
      return [];
    }

    const totals = new Map<ExpenseCostType, number>();
    EXPENSE_COST_TYPES.forEach((costType) => totals.set(costType, 0));

    report.expenses_by_cost_type.forEach((row) => {
      totals.set(row.cost_type, row.total_cents);
    });

    return EXPENSE_COST_TYPES.map((costType) => ({
      cost_type: costType,
      total_cents: totals.get(costType) ?? 0,
    })).filter((row) => row.total_cents > 0);
  }, [report]);

  return (
    <div className="page-content">
      <ToastContainer closeButton={false} limit={1} />
      <Container fluid>
        <BreadCrumb
          title={t("studio.financial.dre.breadcrumb.title")}
          pageTitle={t("studio.financial.dre.breadcrumb.pageTitle")}
        />
        <div className="d-flex justify-content-end mb-3">
          <HelpArticleLink articlePath={HELP_ARTICLE_DRE} />
        </div>

        <Row className="mb-3">
          <Col lg={12}>
            <Card>
              <CardHeader className="d-flex align-items-center flex-wrap gap-2">
                <h4 className="card-title mb-0 flex-grow-1">
                  {t("studio.financial.dre.filters.title")}
                </h4>
                <Button color="soft-primary" tag={Link} to="/financial/expenses">
                  <i className="ri-wallet-3-line align-bottom me-1" />
                  {t("studio.financial.dre.actions.manageExpenses")}
                </Button>
              </CardHeader>
              <CardBody>
                <Row className="g-3 align-items-end">
                  <Col md={4}>
                    <Label htmlFor="dre-unit">{t("studio.financial.dre.fields.unit.label")}</Label>
                    <Input
                      id="dre-unit"
                      type="select"
                      value={unitId}
                      onChange={(event) => setUnitId(event.target.value)}
                      disabled={loadingUnits}
                    >
                      <option value="">{t("studio.financial.dre.fields.unit.placeholder")}</option>
                      {units.map((unit) => (
                        <option key={unit.id} value={unit.id}>
                          {unit.name}
                        </option>
                      ))}
                    </Input>
                  </Col>
                  <Col md={3}>
                    <Label htmlFor="dre-from">{t("studio.financial.dre.fields.from.label")}</Label>
                    <Input
                      id="dre-from"
                      type="date"
                      value={fromDate}
                      onChange={(event) => setFromDate(event.target.value)}
                    />
                  </Col>
                  <Col md={3}>
                    <Label htmlFor="dre-to">{t("studio.financial.dre.fields.to.label")}</Label>
                    <Input
                      id="dre-to"
                      type="date"
                      value={toDate}
                      onChange={(event) => setToDate(event.target.value)}
                    />
                  </Col>
                  <Col md={2}>
                    <Button
                      color="primary"
                      className="w-100"
                      onClick={fetchReport}
                      disabled={loadingReport || loadingUnits}
                    >
                      {loadingReport ? (
                        <Spinner size="sm" />
                      ) : (
                        t("studio.financial.dre.actions.generate")
                      )}
                    </Button>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {loadingReport ? (
          <div className="text-center py-5">
            <Spinner color="primary">{t("studio.financial.dre.loading")}</Spinner>
          </div>
        ) : report ? (
          <>
            <Row className="mb-3">
              <Col lg={12}>
                <p className="text-muted mb-0">
                  {t("studio.financial.dre.summary.subtitle", {
                    unit: resolveUnitName(report.unit_id),
                    period: formatPeriodLabel(
                      report.period.from,
                      report.period.to,
                      i18n.language
                    ),
                  })}
                </p>
              </Col>
            </Row>

            <Row className="g-3 mb-4">
              {summaryCards.map((card) => (
                <Col md={4} key={card.id}>
                  <Card className="card-animate">
                    <CardBody>
                      <div className="d-flex align-items-center">
                        <div className="flex-grow-1">
                          <p className="text-uppercase fw-medium text-muted mb-1">{card.label}</p>
                          <h4 className={`fs-22 fw-semibold mb-0 text-${card.color}`}>
                            {formatMoney(card.value)}
                          </h4>
                        </div>
                        <div className="avatar-sm flex-shrink-0">
                          <span className={`avatar-title bg-${card.color}-subtle rounded fs-3`}>
                            <i className={`${card.icon} text-${card.color}`} />
                          </span>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              ))}
            </Row>

            <Row className="g-3">
              <Col lg={6}>
                <Card>
                  <CardHeader>
                    <h5 className="card-title mb-0">
                      {t("studio.financial.dre.tables.byCategory")}
                    </h5>
                  </CardHeader>
                  <CardBody>
                    {categoryRows.length === 0 ? (
                      <p className="text-muted mb-0">{t("studio.financial.dre.empty.breakdown")}</p>
                    ) : (
                      <div className="table-responsive">
                        <Table className="align-middle mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>{t("studio.financial.dre.tables.category")}</th>
                              <th className="text-end">{t("studio.financial.dre.tables.amount")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {categoryRows.map((row) => (
                              <tr key={row.category}>
                                <td>{resolveCategoryLabel(row.category)}</td>
                                <td className="text-end">{formatMoney(row.total_cents)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
              <Col lg={6}>
                <Card>
                  <CardHeader>
                    <h5 className="card-title mb-0">
                      {t("studio.financial.dre.tables.byCostType")}
                    </h5>
                  </CardHeader>
                  <CardBody>
                    {costTypeRows.length === 0 ? (
                      <p className="text-muted mb-0">{t("studio.financial.dre.empty.breakdown")}</p>
                    ) : (
                      <div className="table-responsive">
                        <Table className="align-middle mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>{t("studio.financial.dre.tables.costType")}</th>
                              <th className="text-end">{t("studio.financial.dre.tables.amount")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {costTypeRows.map((row) => (
                              <tr key={row.cost_type}>
                                <td>{resolveCostTypeLabel(row.cost_type)}</td>
                                <td className="text-end">{formatMoney(row.total_cents)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </>
        ) : hasSearched ? null : (
          <div className="text-center py-5">
            <p className="text-muted mb-0">{t("studio.financial.dre.empty.initial")}</p>
          </div>
        )}
      </Container>
    </div>
  );
};

export default DreReport;
